import axios, { AxiosInstance, AxiosError, AxiosRequestHeaders } from 'axios';
import uuid from 'uuid/v4';
import isNil from 'lodash/isNil';
import VueRouter, { Route } from 'vue-router';
import { Store } from 'vuex';

import { Config } from '@/environment';
import { AppState } from '@/store';
import { AppUiService } from './ui/ui.service';
import { Logger } from './logger';
import { CustomerType } from '@/models';
import { Routes } from '@/router';

import { MUTATIONS as CUSTOMER_MUTATIONS } from '@/store/customer';
import { MUTATIONS as UI_MUTATIONS } from '@/store/ui';
import { HEADER_KEY_CORRELATION_ID, HEADER_KEY_KASADA_BYPASS } from './api/api.service';
import { makeAuthorizationHeader, getCountryByHostname, getCmsBaseUrlByCountryCode, isLocal } from '@/helpers';
import { CountryCode } from '@/store/ui';

export function makeCmsHttpClient(config: Config): AxiosInstance {
  const countryCode = getCountryByHostname(window.location.hostname).countryCode;
  return axios.create({
    baseURL: getCmsBaseUrlByCountryCode(config, countryCode),
    headers: {
      'Content-Type': 'application/json',
    },
  });
}

export function makeApiGatewayHttpClient(config: Config, store: Store<AppState>, uiService: AppUiService, router: VueRouter, logger: Logger): AxiosInstance {
  const countryCode = getCountryByHostname(window.location.hostname).countryCode;
  const client = axios.create({
    baseURL: getGatewayBaseUrlByCountryCode(config, countryCode),
    headers: {
      'Ocp-Apim-Subscription-Key': getGatewaySubscriptionKeyByCountryCode(config, countryCode),
      'Content-Type': 'application/json',
    },
  });

  // Automatically add authorization and correlation id headers
  client.interceptors.request.use(req => {
    if (req.baseURL) {
      logger.debug(`Start API request: ${req.baseURL.substring(0, req.baseURL.length - 1)}${req.url}. Correlation ID: ${req.headers[HEADER_KEY_CORRELATION_ID]}`);
    }

    makeCorrelationIdHeader(req.headers);
    makeAuthorizationHeader(req.headers, store.state.customer.jwt);

    if (isLocal()) {
      makeKasadaByPassHeader(req.headers);
    }

    return req;
  });

  // Automatically drop jwt on 401
  client.interceptors.response.use(
    res => {
      logger.debug(`End API request: ${res.config.url}`);
      return res;
    },
    (err: AxiosError | any) => {
      const status = err && err.response && err.response.status;
      if (status === 401) {
        const profile = store.state.customer.profile;
        // TODO-d8ehf5: maybe find another way to do this? I don't like bypassing
        // the services like this, but I can't think of another way that doesn't
        // introduce the possibility of infinite loops (aside from directly
        // exposing a method to set the jwt and pass in a getter fn to the enclosing method?)
        store.commit(CUSTOMER_MUTATIONS.SET_JWT, undefined);
        store.commit(CUSTOMER_MUTATIONS.RESET_PROFILE);

        // Close any existing modal or bag so the user lands on the undisturbed sign-in page
        uiService.closeAllOverlays();
        const showSessionExpiredBanner = (profile && !profile.isGuest) && ![
          Routes.VendorSignIn,
          Routes.VendorCreateAccount,
          Routes.VendorPasswordResetRequestPage,
          Routes.Home,
          Routes.PasswordResetPage,
          Routes.PasswordResetRequestPage,
          Routes.SignIn,
          Routes.CreateAccount,
          Routes.NutritionCalculatorPage,
          Routes.NutritionCalculcatorMealBuilder,
        ].includes(router.currentRoute.name as Routes);

        store.commit(UI_MUTATIONS.SET_SESSION_EXPIRED_BANNER, showSessionExpiredBanner);

        return Promise.reject(err);
      }
      return Promise.reject(err);
    },
  );

  return client;
}

function makeKasadaByPassHeader(headers: AxiosRequestHeaders) {
  headers[HEADER_KEY_KASADA_BYPASS] =  `hJ3Q^lVB#w1o`;
}

function makeCorrelationIdHeader(headers: AxiosRequestHeaders) {
  headers[HEADER_KEY_CORRELATION_ID] =  `OrderWeb-${uuid()}`;
}

function getGatewayBaseUrlByCountryCode(config: Config, countryCode: string): string {
  switch (countryCode) {
    case CountryCode.GB:
      return config.services.gatewayUrlUk
    case CountryCode.CA:
    case CountryCode.US:
    default:
      return config.services.gatewayUrl
  }
}

function getGatewaySubscriptionKeyByCountryCode(config: Config, countryCode: string): string {
  switch (countryCode) {
    case CountryCode.GB:
      return config.services.gatewaySubscriptionKeyUk
    case CountryCode.CA:
    case CountryCode.US:
    default:
      return config.services.gatewaySubscriptionKey;
  }
}